import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Calculator, Award, Phone, TrendingUp, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import CommissionDisclosure from '../components/CommissionDisclosure';
import SEO from '../components/SEO';

// Counter Animation Hook
const useCountUp = (end, duration = 2000, start = 0) => {
  const [count, setCount] = useState(start);
  const [isVisible, setIsVisible] = useState(false);
  const countRef = useRef(null);

  useEffect(() => {
    const currentRef = countRef.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible) return;

    let startTimestamp = null;
    const step = (timestamp) => {
      if (!startTimestamp) startTimestamp = timestamp;
      const progress = Math.min((timestamp - startTimestamp) / duration, 1);
      
      // Easing function for smooth animation
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      setCount(Math.floor(easeOutQuart * (end - start) + start));

      if (progress < 1) {
        window.requestAnimationFrame(step);
      }
    };

    window.requestAnimationFrame(step);
  }, [isVisible, end, duration, start]);

  return { count, countRef };
};

const HomePage = () => {
  const stat1 = useCountUp(100, 2500);
  const stat2 = useCountUp(500, 2500);
  const stat3 = useCountUp(17, 2000);
  return (
    <div>
      <SEO 
        title="Rubicon Finservices | AMFI Registered Mutual Fund Distributor | ARN: 102867"
        description="Rubicon Finservices - AMFI Registered Mutual Fund Distributor (ARN: 102867) with 17+ years of experience. Expert investment advice, SIP planning, retirement solutions. ₹100 Cr+ AUM, 500+ happy investors."
        keywords="mutual funds, mutual fund distributor, SIP investment, retirement planning, AMFI registered, ARN 102867, wealth management, financial planning, investment advisory, mutual fund India, SIP calculator"
        ogImage="https://customer-assets.emergentagent.com/job_rubicon-funds/artifacts/7akuxub4_LOGO.png"
      />
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-900 via-blue-800 to-purple-900 text-white py-32 overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight">
              Your Trusted Partner in
              <span className="block text-amber-400">Wealth Creation</span>
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-blue-100">
              Expert mutual fund distribution services with over 17+ years of experience
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link to="/contact">
                <Button size="lg" className="bg-amber-600 hover:bg-amber-700 text-white px-8 py-6 text-lg">
                  Get Started
                  <ArrowRight className="ml-2" size={20} />
                </Button>
              </Link>
              <Link to="/calculators">
                <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-blue-900 px-8 py-6 text-lg">
                  Try Calculators
                </Button>
              </Link>
            </div>
          </div>

          {/* Stats with Animation */}
          <div className="grid md:grid-cols-3 gap-8 mt-20 max-w-4xl mx-auto">
            <div ref={stat1.countRef} className="text-center transform transition-all duration-500 hover:scale-110">
              <div className="text-5xl font-bold text-amber-400 mb-2 tabular-nums">
                ₹{stat1.count} Cr+
              </div>
              <div className="text-blue-200">Assets Under Management</div>
            </div>
            <div ref={stat2.countRef} className="text-center transform transition-all duration-500 hover:scale-110">
              <div className="text-5xl font-bold text-amber-400 mb-2 tabular-nums">
                {stat2.count}+
              </div>
              <div className="text-blue-200">Happy Investors</div>
            </div>
            <div ref={stat3.countRef} className="text-center transform transition-all duration-500 hover:scale-110">
              <div className="text-5xl font-bold text-amber-400 mb-2 tabular-nums">
                {stat3.count}+
              </div>
              <div className="text-blue-200">Years of Excellence</div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Intro */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
              Welcome to Rubicon Finservices
            </h2>
            <p className="text-xl text-slate-600 leading-relaxed mb-8">
              We are AMFI registered mutual fund distributors (ARN: 102867) committed to helping you 
              achieve your financial goals through expert guidance and personalized investment solutions. 
              With over 17 years of experience and partnerships with India's top 20 AMCs.
            </p>
            <div className="inline-block bg-amber-100 text-amber-700 px-6 py-3 rounded-full font-semibold">
              AMFI Registered • ARN: 102867 • APMI: APMI-APRN01145
            </div>
          </div>
        </div>
      </section>

      {/* Compliance & Transparency Section */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <div className="inline-block bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
                Transparency & Compliance
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Your Trust, Our Priority
              </h2>
              <p className="text-lg text-slate-600">
                We believe in complete transparency and regulatory compliance
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {/* Investor Grievances */}
              <Card className="border-2 border-slate-200 bg-white">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-red-600">
                      <rect width="20" height="16" x="2" y="4" rx="2"/>
                      <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">Investor Grievances</h3>
                  <p className="text-slate-600 text-sm mb-4">
                    Have a concern? We're here to help resolve any issues promptly
                  </p>
                  <a 
                    href="mailto:complaints@rubiconfinservices.com"
                    className="inline-flex items-center gap-2 bg-red-50 hover:bg-red-100 border-2 border-red-200 text-red-700 px-5 py-2.5 rounded-lg font-semibold transition-all hover:shadow-md text-sm"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect width="20" height="16" x="2" y="4" rx="2"/>
                      <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
                    </svg>
                    complaints@rubiconfinservices.com
                  </a>
                </CardContent>
              </Card>

              {/* Commission Disclosure */}
              <Card className="border-2 border-slate-200 bg-white">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-blue-600">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                      <polyline points="14 2 14 8 20 8"/>
                      <line x1="16" y1="13" x2="8" y2="13"/>
                      <line x1="16" y1="17" x2="8" y2="17"/>
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">Commission Disclosure</h3>
                  <p className="text-slate-600 text-sm mb-4">
                    Complete transparency on how we earn and our regulatory compliance
                  </p>
                  <CommissionDisclosure 
                    triggerClassName="inline-flex items-center gap-2 bg-blue-50 hover:bg-blue-100 border-2 border-blue-200 text-blue-700 px-5 py-2.5 rounded-lg font-semibold transition-all hover:shadow-md text-sm"
                    triggerText="View Disclosure"
                  />
                </CardContent>
              </Card>
            </div>

            {/* Additional Info */}
            <div className="mt-8 bg-white rounded-xl p-6 border-2 border-slate-200">
              <p className="text-slate-600 text-sm text-center mb-4">
                <strong className="text-slate-900">Certified & Regulated:</strong> We are AMFI registered (ARN: 102867) and APMI certified (APMI-APRN01145). 
                All our practices comply with SEBI regulations and AMFI code of conduct.
              </p>
              <div className="pt-4 border-t border-slate-200">
                <p className="text-slate-600 text-xs text-center">
                  <strong className="text-slate-800">MSME Registered:</strong> Rubicon Finservices is registered with the Ministry of Micro, Small and Medium Enterprises, Government of India
                  <span className="block mt-2 text-amber-700 font-semibold">UDYAM Registration No: UDYAM-WB-14-0192124</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Preview Cards */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 text-center mb-16">
            Explore Our Services
          </h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-7xl mx-auto">
            {/* About Preview */}
            <Link to="/about" className="group">
              <Card className="h-full border-2 border-slate-200 hover:border-blue-500 hover:shadow-2xl transition-all duration-300">
                <CardContent className="p-8 text-center">
                  <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
                    <Users className="text-white" size={40} />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-4">About Us</h3>
                  <p className="text-slate-600 mb-6">
                    Learn about our 17+ years journey, awards, and professional credentials
                  </p>
                  <div className="flex items-center justify-center text-blue-600 font-semibold group-hover:gap-3 gap-2 transition-all">
                    Learn More
                    <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                  </div>
                </CardContent>
              </Card>
            </Link>

            {/* Services Preview */}
            <Link to="/services" className="group">
              <Card className="h-full border-2 border-slate-200 hover:border-amber-500 hover:shadow-2xl transition-all duration-300">
                <CardContent className="p-8 text-center">
                  <div className="w-20 h-20 bg-gradient-to-br from-amber-500 to-amber-600 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
                    <TrendingUp className="text-white" size={40} />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-4">Services</h3>
                  <p className="text-slate-600 mb-6">
                    Mutual Funds, SIP, AIF, PMS, and partnerships with top 20 AMCs
                  </p>
                  <div className="flex items-center justify-center text-amber-600 font-semibold group-hover:gap-3 gap-2 transition-all">
                    View Services
                    <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                  </div>
                </CardContent>
              </Card>
            </Link>

            {/* Calculators Preview */}
            <Link to="/calculators" className="group">
              <Card className="h-full border-2 border-slate-200 hover:border-green-500 hover:shadow-2xl transition-all duration-300">
                <CardContent className="p-8 text-center">
                  <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
                    <Calculator className="text-white" size={40} />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-4">Calculators</h3>
                  <p className="text-slate-600 mb-6">
                    SIP, Retirement, and STP calculators to plan your financial future
                  </p>
                  <div className="flex items-center justify-center text-green-600 font-semibold group-hover:gap-3 gap-2 transition-all">
                    Start Planning
                    <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                  </div>
                </CardContent>
              </Card>
            </Link>

            {/* Contact Preview */}
            <Link to="/contact" className="group">
              <Card className="h-full border-2 border-slate-200 hover:border-purple-500 hover:shadow-2xl transition-all duration-300">
                <CardContent className="p-8 text-center">
                  <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
                    <Phone className="text-white" size={40} />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-4">Contact Us</h3>
                  <p className="text-slate-600 mb-6">
                    Get in touch for personalized investment advice and support
                  </p>
                  <div className="flex items-center justify-center text-purple-600 font-semibold group-hover:gap-3 gap-2 transition-all">
                    Get In Touch
                    <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                  </div>
                </CardContent>
              </Card>
            </Link>
          </div>
        </div>
      </section>

      {/* Latest from the Blog */}
      <section className="py-20 bg-white" data-testid="home-blog-section">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <div className="inline-block bg-amber-100 text-amber-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
                Blog & Resources
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Latest from Our Blog</h2>
            </div>
            <Link to="/blog/sip-kolkata-guide" className="group block" data-testid="home-blog-card">
              <Card className="border-2 border-slate-200 hover:border-amber-500 hover:shadow-2xl transition-all duration-300">
                <CardContent className="p-8">
                  <span className="bg-amber-100 text-amber-700 px-3 py-1 rounded-full font-semibold text-xs">SIP Basics</span>
                  <h3 className="text-2xl font-bold text-slate-900 mt-4 mb-3 group-hover:text-amber-600 transition-colors">
                    How to Start a SIP in Kolkata: A Practical Guide
                  </h3>
                  <p className="text-slate-600 mb-5">
                    Practical steps, local considerations, and how a Kolkata-based mutual fund distributor can help you begin systematic investing.
                  </p>
                  <span className="inline-flex items-center gap-2 text-amber-600 font-semibold group-hover:gap-3 transition-all">
                    Read Article <ArrowRight size={18} />
                  </span>
                </CardContent>
              </Card>
            </Link>
            <div className="text-center mt-6">
              <Link to="/blog" className="text-blue-700 font-semibold hover:text-amber-600 transition-colors" data-testid="home-blog-all-link">
                View all articles →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-900 to-purple-900 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Ready to Start Your Investment Journey?
            </h2>
            <p className="text-xl text-blue-100 mb-8">
              Connect with our expert advisors and take the first step towards financial success
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link to="/contact">
                <Button size="lg" className="bg-amber-600 hover:bg-amber-700 text-white px-8 py-6 text-lg">
                  Contact Us Today
                  <ArrowRight className="ml-2" size={20} />
                </Button>
              </Link>
              <Link to="/calculators">
                <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-blue-900 px-8 py-6 text-lg">
                  Try Our Calculators
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
